import { useEffect, useState } from 'react';
import { Box, CircularProgress, IconButton, Modal, Stack, TextField, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CustomText from 'components/common/CustomText/CustomText';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { colors } from 'styles/colors';
import {
  AttributePart,
  RawProductAttributeName,
  RawProductAttributes,
  RawProductScent,
  RawProductShade,
  reextractRawProductAttributePart,
} from 'services/rawProductService';
import { usePressToSave } from './usePressToSave';

interface Props {
  attributes: RawProductAttributes | null;
  rawProductId: string;
  open: boolean;
  productName: string;
  isSaving: boolean;
  onClose: () => void;
  onSave: (attributes: RawProductAttributes) => void;
  // What a reload answered, so the card behind the modal shows the same lists
  // the reviewer is now looking at rather than the ones it opened with.
  onReloaded?: (attributes: RawProductAttributes | null) => void;
  // One part of the pack's claims on its own, opened from the pencil beside
  // that part on the card. Null is every part at once, which is what the pencil
  // on the heading opens. What Save writes is the whole set either way - the
  // parts not on show are the stored ones, untouched - so this narrows what a
  // reviewer looks at and never what they are about to write.
  section?: AttributeSection | null;
}

// The eight lists, in the order a reviewer reads them: who the product is for,
// when it is used, what it acts on, what it promises, and what it says about its
// own formula.
const LISTS = [
  { key: 'hairTypes', label: 'Hair type', part: 'types', hint: 'Only where the pack names one or the product settles it.' },
  { key: 'skinTypes', label: 'Skin type', part: 'types', hint: 'Only where the pack names one or the product settles it.' },
  { key: 'scalpTypes', label: 'Scalp type', part: 'types', hint: 'Only where the pack names one or the product settles it.' },
  { key: 'beardTypes', label: 'Beard type', part: 'types', hint: 'Only for a beard product.' },
  { key: 'usageTimings', label: 'Usage timing', part: 'timings', hint: 'A moment - after a shower, before bed - never a frequency.' },
  { key: 'concerns', label: 'Concern', part: 'concerns', hint: 'What is wrong that the product is bought to deal with.' },
  { key: 'goals', label: 'Goal', part: 'goals', hint: 'What the buyer gets, named as the outcome itself.' },
  { key: 'formulaClaims', label: 'Formula claim', part: 'formula-claims', hint: 'What the formula is free of, how it was made, or what it was tested or certified as.' },
] as const;

type ListKey = (typeof LISTS)[number]['key'];
type Draft = Record<ListKey, RawProductAttributeName[]>;

// What can be edited on its own: any one of the lists, and each of the three
// the colour pass fills.
export type AttributeSection = ListKey | 'scent' | 'flavor' | 'colors';

// What the modal is called while it is showing one part alone.
const SECTION_LABELS: Record<AttributeSection, string> = {
  hairTypes: 'hair types',
  skinTypes: 'skin types',
  scalpTypes: 'scalp types',
  beardTypes: 'beard types',
  usageTimings: 'usage timings',
  concerns: 'concerns',
  goals: 'goals',
  formulaClaims: 'formula claims',
  scent: 'scent',
  flavor: 'flavour',
  colors: 'colours',
};

// A colour is four values a pack prints in any combination, and a product can
// come in several - one row for an ordinary colour product, one per shade for a
// palette. The code is the one field with no Arabic: it is an identifier rather
// than language.
interface ShadeDraft {
  family: RawProductAttributeName;
  shade: RawProductAttributeName;
  code: string;
  undertone: RawProductAttributeName;
}

const EMPTY_NAME: RawProductAttributeName = { en: '', ar: '' };
const EMPTY_SHADE: ShadeDraft = {
  family: EMPTY_NAME,
  shade: EMPTY_NAME,
  code: '',
  undertone: EMPTY_NAME,
};
const SHADE_FIELDS = ['family', 'undertone', 'shade'] as const;

function toDraft(attributes: RawProductAttributes | null): Draft {
  return {
    hairTypes: attributes?.suitability?.hairTypes ?? [],
    skinTypes: attributes?.suitability?.skinTypes ?? [],
    scalpTypes: attributes?.suitability?.scalpTypes ?? [],
    beardTypes: attributes?.suitability?.beardTypes ?? [],
    usageTimings: attributes?.suitability?.usageTimings ?? [],
    concerns: attributes?.claims?.concerns ?? [],
    goals: attributes?.claims?.goals ?? [],
    formulaClaims: attributes?.formulaClaims ?? [],
  };
}

// The scent is one value, not a list: a pack comes in one fragrance, and a
// second fragrance is a second pack with its own barcode.
interface ScentDraft {
  family: RawProductAttributeName;
  name: RawProductAttributeName;
}

function toScent(attributes: RawProductAttributes | null): ScentDraft {
  const scent: RawProductScent | null = attributes?.scent ?? null;

  return { family: scent?.family ?? EMPTY_NAME, name: scent?.name ?? EMPTY_NAME };
}

// The same two levels as the scent, for what it tastes of - and answered for the
// few products that are tasted rather than smelled.
function toFlavor(attributes: RawProductAttributes | null): ScentDraft {
  const flavor = attributes?.flavor ?? null;

  return { family: flavor?.family ?? EMPTY_NAME, name: flavor?.name ?? EMPTY_NAME };
}

function toShades(attributes: RawProductAttributes | null): ShadeDraft[] {
  return (attributes?.colors ?? []).map((color: RawProductShade) => ({
    family: color.family ?? EMPTY_NAME,
    shade: color.shade ?? EMPTY_NAME,
    code: color.code ?? '',
    undertone: color.undertone ?? EMPTY_NAME,
  }));
}

// Both languages or the value is dropped: the English is what everything
// downstream matches on and the Arabic is what the Arabic screen shows, so one
// without the other is missing wherever the other is read.
const filled = (name: RawProductAttributeName) => Boolean(name.en.trim() && name.ar.trim());
const trimmed = (name: RawProductAttributeName) => ({ en: name.en.trim(), ar: name.ar.trim() });

export default function AttributesModal({
  attributes,
  rawProductId,
  open,
  productName,
  isSaving,
  onClose,
  onSave,
  onReloaded,
  section = null,
}: Props) {
  const [draft, setDraft] = useState<Draft>(toDraft(null));
  const { saving, press } = usePressToSave(isSaving, open);
  const [shades, setShades] = useState<ShadeDraft[]>([]);
  const [scent, setScent] = useState<ScentDraft>(toScent(null));
  const [flavor, setFlavor] = useState<ScentDraft>(toFlavor(null));
  // Which pass is being read again, so its heading shows a spinner and the rest
  // of the modal stays usable. One at a time: a reviewer who fires all six at
  // once is paying for the whole re-read they were avoiding.
  const [reloading, setReloading] = useState<AttributePart | null>(null);
  const [reloaded, setReloaded] = useState<RawProductAttributes | null>(null);

  // Opened on what the row holds every time rather than on whatever was left
  // here last: an abandoned edit that came back on the next open would read as a
  // stored value.
  useEffect(() => {
    if (!open) return;
    setReloaded(null);
    setDraft(toDraft(attributes));
    setShades(toShades(attributes));
    setScent(toScent(attributes));
    setFlavor(toFlavor(attributes));
  }, [open, attributes]);

  // A heading's own pass, read again off the pack. What comes back is the whole
  // column, and only the fields that pass answers are taken from it: the rest of
  // the modal is the reviewer's edit in progress, which a reload of one heading
  // has no business touching.
  const reload = async (part: AttributePart) => {
    if (reloading || saving) return;
    setReloading(part);

    try {
      const result = await reextractRawProductAttributePart({ rawProductId, part });
      const next = result?.attributes ?? null;
      const read = toDraft(next);

      setDraft((existing) => ({
        ...existing,
        ...(part === 'types'
          ? {
              hairTypes: read.hairTypes,
              skinTypes: read.skinTypes,
              scalpTypes: read.scalpTypes,
              beardTypes: read.beardTypes,
            }
          : {}),
        ...(part === 'timings' ? { usageTimings: read.usageTimings } : {}),
        ...(part === 'concerns' ? { concerns: read.concerns } : {}),
        ...(part === 'goals' ? { goals: read.goals } : {}),
        ...(part === 'formula-claims' ? { formulaClaims: read.formulaClaims } : {}),
      }));

      if (part === 'appearance') {
        setShades(toShades(next));
        setScent(toScent(next));
        setFlavor(toFlavor(next));
      }

      // The re-read is already stored, so the card behind this modal is out of
      // date the moment it answers.
      setReloaded(next);
      onReloaded?.(next);
    } finally {
      setReloading(null);
    }
  };

  // A heading and the reload beside it. The tooltip names the pass rather than
  // the heading, because one pass answers several headings and a reviewer
  // reloading the hair type is about to watch the skin type change too.
  const heading = (label: string, part: AttributePart, note: string, clear?: () => void) => (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <CustomText text={label} fontSize={13} fontWeight={600} color={colors.primary} />
      <Tooltip title={reloading === part ? 'Reading the pack…' : note}>
        <span>
          <IconButton
            size="small"
            disabled={saving || Boolean(reloading)}
            onClick={() => reload(part)}
          >
            {reloading === part ? (
              <CircularProgress size={13} />
            ) : (
              <RefreshIcon sx={{ fontSize: 15, color: colors.gray }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
      {/* A list drops an entry by the bin on its row; these two are one value
          spread over four boxes, and emptying them by hand is the same edit
          made four times. */}
      {clear && (
        <Tooltip title="Clear">
          <span>
            <IconButton size="small" disabled={saving} onClick={clear}>
              <DeleteOutlineIcon sx={{ fontSize: 15, color: colors.gray }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Stack>
  );

  // What the row holds now: the last reload's answer where there has been one,
  // and otherwise what the modal opened on. Without this a reviewer who reloads
  // and changes nothing is offered a Save that would write back what is already
  // stored.
  const stored = reloaded ?? attributes;

  const changed =
    JSON.stringify(draft) !== JSON.stringify(toDraft(stored)) ||
    JSON.stringify(shades) !== JSON.stringify(toShades(stored)) ||
    JSON.stringify(scent) !== JSON.stringify(toScent(stored)) ||
    JSON.stringify(flavor) !== JSON.stringify(toFlavor(stored));

  // One entry moved a place up or down its list.
  //
  // The order is the order these are shown in, and a reviewer reading a product
  // page wants the claim the pack leads with at the top. Both languages move
  // together - they are one entry with two halves, and a list that reordered one
  // of them would pair every name with somebody else's.
  const move = (key: ListKey, row: number, by: -1 | 1) =>
    setDraft((existing) => {
      const list = [...existing[key]];
      const to = row + by;
      if (to < 0 || to >= list.length) return existing;

      [list[row], list[to]] = [list[to], list[row]];

      return { ...existing, [key]: list };
    });

  // A pair of boxes, English then Arabic, the way every other part of this card
  // shows the two languages of one thing.
  const pair = (
    value: RawProductAttributeName,
    onChange: (language: 'en' | 'ar', next: string) => void,
    placeholder: string,
  ) => (
    <>
      <TextField
        fullWidth
        size="small"
        value={value.en}
        disabled={saving}
        onChange={(event) => onChange('en', event.target.value)}
        placeholder={`${placeholder} (EN)`}
      />
      <TextField
        fullWidth
        size="small"
        value={value.ar}
        disabled={saving}
        onChange={(event) => onChange('ar', event.target.value)}
        placeholder={`${placeholder} (AR)`}
        inputProps={{ dir: 'rtl' }}
      />
    </>
  );

  return (
    <Modal open={open} onClose={saving ? undefined : onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
          width: 820,
          maxWidth: '94vw',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <CustomText
          text={section ? `Edit ${SECTION_LABELS[section]}` : 'Edit attributes'}
          fontWeight={700}
          fontSize={18}
        />
        <Box mt={0.5}>
          <CustomText text={productName} fontSize={13} color={colors.gray} />
        </Box>

        {LISTS.filter(({ key }) => !section || section === key).map(({ key, label, part, hint }) => (
          <Box key={key} mt={2.5}>
            {heading(label, part, 'Read this off the pack again')}
            <Box mt={0.25}>
              <CustomText text={hint} fontSize={11} color={colors.gray} />
            </Box>

            {draft[key].map((name, row) => (
              <Box
                key={`${key}-${row}`}
                mt={1}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' },
                  columnGap: 1.5,
                  rowGap: 1,
                  alignItems: 'center',
                }}
              >
                {pair(
                  name,
                  (language, next) =>
                    setDraft((existing) => ({
                      ...existing,
                      [key]: existing[key].map((entry, position) =>
                        position === row ? { ...entry, [language]: next } : entry,
                      ),
                    })),
                  label,
                )}
                <Stack direction="row" alignItems="center">
                  {/* Disabled at the ends rather than hidden, so the row's
                      controls stay in the same places down the list. */}
                  <Tooltip title="Move up">
                    <span>
                      <IconButton
                        size="small"
                        disabled={saving || row === 0}
                        onClick={() => move(key, row, -1)}
                      >
                        <ArrowUpwardIcon sx={{ fontSize: 16, color: colors.gray }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton
                        size="small"
                        disabled={saving || row === draft[key].length - 1}
                        onClick={() => move(key, row, 1)}
                      >
                        <ArrowDownwardIcon sx={{ fontSize: 16, color: colors.gray }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      disabled={saving}
                      onClick={() =>
                        setDraft((existing) => ({
                          ...existing,
                          [key]: existing[key].filter((_name, position) => position !== row),
                        }))
                      }
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16, color: colors.gray }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            ))}

            <Box mt={1}>
              <CustomButton
                text="Add"
                variant="outlined"
                height={32}
                icon={<AddIcon sx={{ fontSize: 16 }} />}
                disabled={saving}
                onClick={() =>
                  setDraft((existing) => ({ ...existing, [key]: [...existing[key], EMPTY_NAME] }))
                }
              />
            </Box>
          </Box>
        ))}

        {(!section || section === 'scent') && (
          <Box mt={2.5}>
            {heading('Scent', 'appearance', 'Read the colour, scent and flavour off the pack again', () =>
              setScent(toScent(null)),
            )}
            <Box mt={0.25}>
              <CustomText
                text="The family a filter runs on, and the pack's own name for it. Leave both empty where the pack says nothing about how it smells - a stated fragrance-free is a label, not a scent."
                fontSize={11}
                color={colors.gray}
              />
            </Box>
            <Box
              mt={1}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: 1.5,
                rowGap: 1,
              }}
            >
              {(['family', 'name'] as const).map((field) =>
                pair(
                  scent[field],
                  (language, next) =>
                    setScent((existing) => ({
                      ...existing,
                      [field]: { ...existing[field], [language]: next },
                    })),
                  field === 'family' ? 'Family' : 'Scent',
                ),
              )}
            </Box>
          </Box>
        )}

        {(!section || section === 'flavor') && (
          <Box mt={2.5}>
            {heading('Flavour', 'appearance', 'Read the colour, scent and flavour off the pack again', () =>
              setFlavor(toFlavor(null)),
            )}
            <Box mt={0.25}>
              <CustomText
                text="Only for a product that is tasted - toothpaste, mouthwash, a lip product. A scented cream has none, however strongly it smells."
                fontSize={11}
                color={colors.gray}
              />
            </Box>
            <Box
              mt={1}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: 1.5,
                rowGap: 1,
              }}
            >
              {(['family', 'name'] as const).map((field) =>
                pair(
                  flavor[field],
                  (language, next) =>
                    setFlavor((existing) => ({
                      ...existing,
                      [field]: { ...existing[field], [language]: next },
                    })),
                  field === 'family' ? 'Family' : 'Flavour',
                ),
              )}
            </Box>
          </Box>
        )}

        {(!section || section === 'colors') && (
          <Box mt={2.5}>
            {heading('Colour', 'appearance', 'Read the colour, scent and flavour off the pack again')}
            <Box mt={0.25}>
              <CustomText
                text="Only where the colour is what is being bought. The family and undertone are what a filter runs on; the shade and code are what the pack prints. A palette has one row per shade."
                fontSize={11}
                color={colors.gray}
              />
            </Box>

            {shades.map((entry, row) => (
              <Box key={`shade-${row}`} mt={1.5}>
                <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
                  <CustomText
                    text={`Colour ${row + 1}`}
                    fontSize={12}
                    fontWeight={600}
                    color={colors.gray}
                  />
                  <Tooltip title="Remove this colour">
                    <IconButton
                      size="small"
                      disabled={saving}
                      onClick={() =>
                        setShades((existing) =>
                          existing.filter((_shade, position) => position !== row),
                        )
                      }
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16, color: colors.gray }} />
                    </IconButton>
                  </Tooltip>
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    columnGap: 1.5,
                    rowGap: 1,
                  }}
                >
                  {SHADE_FIELDS.map((field) =>
                    pair(
                      entry[field],
                      (language, next) =>
                        setShades((existing) =>
                          existing.map((shade, position) =>
                            position === row
                              ? { ...shade, [field]: { ...shade[field], [language]: next } }
                              : shade,
                          ),
                        ),
                      field.charAt(0).toUpperCase() + field.slice(1),
                    ),
                  )}
                  {/* One box and no language: a shade code is an identifier. */}
                  <TextField
                    fullWidth
                    size="small"
                    value={entry.code}
                    disabled={saving}
                    onChange={(event) =>
                      setShades((existing) =>
                        existing.map((shade, position) =>
                          position === row ? { ...shade, code: event.target.value } : shade,
                        ),
                      )
                    }
                    placeholder="Code"
                  />
                </Box>
              </Box>
            ))}

            <Box mt={1}>
              <CustomButton
                text="Add colour"
                variant="outlined"
                height={32}
                icon={<AddIcon sx={{ fontSize: 16 }} />}
                disabled={saving}
                onClick={() => setShades((existing) => [...existing, EMPTY_SHADE])}
              />
            </Box>
          </Box>
        )}

        <Box mt={2}>
          <CustomText
            text="Both languages or the value is dropped: the English is what search matches on, the Arabic is what an Arabic shopper reads."
            fontSize={11}
            color={colors.gray}
          />
        </Box>

        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
          <CustomButton
            text="Cancel"
            variant="outlined"
            height={40}
            disabled={saving}
            onClick={onClose}
          />
          <CustomButton
            text="Save"
            height={40}
            isLoading={saving}
            disabled={!changed}
            onClick={() => press(() => onSave({
                suitability: {
                  hairTypes: draft.hairTypes.filter(filled).map(trimmed),
                  skinTypes: draft.skinTypes.filter(filled).map(trimmed),
                  scalpTypes: draft.scalpTypes.filter(filled).map(trimmed),
                  beardTypes: draft.beardTypes.filter(filled).map(trimmed),
                  usageTimings: draft.usageTimings.filter(filled).map(trimmed),
                },
                claims: {
                  concerns: draft.concerns.filter(filled).map(trimmed),
                  goals: draft.goals.filter(filled).map(trimmed),
                },
                formulaClaims: draft.formulaClaims.filter(filled).map(trimmed),
                // A row with nothing in it is not a colour, so it is not sent:
                // an empty list is how a product says its colour is not what
                // anybody is buying.
                colors: shades
                  .map((entry) => ({
                    family: filled(entry.family) ? trimmed(entry.family) : null,
                    shade: filled(entry.shade) ? trimmed(entry.shade) : null,
                    code: entry.code.trim() || null,
                    undertone: filled(entry.undertone) ? trimmed(entry.undertone) : null,
                  }))
                  .filter((entry) => entry.family || entry.shade || entry.code),
                // Nothing filled in is a pack that said nothing about how it
                // smells, which is a different answer from one saying it has no
                // fragrance - that one is a label.
                scent:
                  filled(scent.family) || filled(scent.name)
                    ? {
                        family: filled(scent.family) ? trimmed(scent.family) : null,
                        name: filled(scent.name) ? trimmed(scent.name) : null,
                      }
                    : null,
                // Null for every product that is not tasted, which is nearly all
                // of them.
                flavor:
                  filled(flavor.family) || filled(flavor.name)
                    ? {
                        family: filled(flavor.family) ? trimmed(flavor.family) : null,
                        name: filled(flavor.name) ? trimmed(flavor.name) : null,
                      }
                    : null,
              }))}
          />
        </Stack>
      </Box>
    </Modal>
  );
}
